import { useEffect, useRef, useMemo } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { PostProcessing as ThreePostProcessing } from 'three/webgpu'
import { pass, mrt, output, emissive, uniform, mix } from 'three/tsl'
import { bloom } from 'three/addons/tsl/display/BloomNode.js'
import { lensflare } from 'three/addons/tsl/display/LensflareNode.js'
import { gaussianBlur } from 'three/addons/tsl/display/GaussianBlurNode.js'
import { radialRGBShift } from '../dsl_effects/effects/RadialRGBShift.js'
import { crt } from '../dsl_effects/effects/CRTNode.js'
import { useControls } from 'leva'
import { cameraTransitionState } from '../camera/Camera'

/**
 * WebGPU Post-Processing using TSL
 * 
 * @param {Object} props
 * @param {boolean} props.enabled - Enable/disable post-processing
 * @param {Object} props.bloomConfig - Bloom configuration
 * @param {Object} props.lensflareConfig - Lensflare configuration
 * @param {Object} props.chromaticAberrationConfig - Chromatic aberration configuration
 */
export default function PostProcessing({ 
  enabled = true,
  bloomConfig = {
    strength: 0.75,
    threshold: 0.5,
    radius: 0.2
  },
  lensflareConfig = {
    enabled: true,
    threshold: 0.5,
    ghostAttenuationFactor: 25,
    ghostSpacing: 0.25,
    blur: 8
  },
  chromaticAberrationConfig = {
    enabled: false,
    amount: 0.005,
    angle: 0,
    radialIntensity: 1.0 // How much the effect increases toward edges
  },
  crtConfig = {
    enabled: false,
    scanlineIntensity: 0.45,
    scanlineCount: 850,
    noiseIntensity: 0.08,
    distortionIntensity: 0.03,
    vignetteIntensity: 0.5
  }
}) {
  const { gl, scene, camera, size, invalidate } = useThree()

  const postProcessingRef = useRef(null)
  const bloomPassRef = useRef(null)
  const crtTimeRef = useRef(0)
  const crtNodeRef = useRef(null)
  const outputNodeRef = useRef(null) // Store base output node
  const scenePassColorRef = useRef(null) // Store scene pass for CRT application
  
  // Track all TSL nodes for proper disposal
  const scenePassRef = useRef(null)
  const lensflarePassRef = useRef(null)
  const chromaticAbPassRef = useRef(null)
  const emissiveBloomRef = useRef(null)
  const flareBlurRef = useRef(null)
  
  // Check if dev mode is enabled via URL parameter
  const isDevMode = useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('dev') === 'true'
  }, [])
  
  // Create uniform nodes that persist across renders
  const uniformsRef = useRef({
    bloomThreshold: uniform(bloomConfig.threshold),
    bloomStrength: uniform(bloomConfig.strength),
    bloomRadius: uniform(bloomConfig.radius),
    lensflareThreshold: uniform(lensflareConfig.threshold),
    lensflareGhostAttenuation: uniform(lensflareConfig.ghostAttenuationFactor),
    lensflareGhostSpacing: uniform(lensflareConfig.ghostSpacing),
    chromaticAbAmount: uniform(chromaticAberrationConfig.amount),
    chromaticAbAngle: uniform(chromaticAberrationConfig.angle),
    chromaticAbRadialIntensity: uniform(chromaticAberrationConfig.radialIntensity),
    crtScanlineIntensity: uniform(crtConfig.scanlineIntensity),
    crtScanlineCount: uniform(crtConfig.scanlineCount),
    crtNoiseIntensity: uniform(crtConfig.noiseIntensity),
    crtDistortionIntensity: uniform(crtConfig.distortionIntensity),
    crtVignetteIntensity: uniform(crtConfig.vignetteIntensity),
    // CRT blend opacity: 0 = invisible (no transition), 1 = fully visible.
    // Driven by useFrame lerp so the CRT node is always built into the pipeline
    // and never needs to be created/disposed on each transition — eliminating the
    // secondary TSL pipeline rebuild that caused a micro-spike on every transition.
    crtOpacity: uniform(0)
  })

  // Leva controls for all post-processing effects (only in dev mode)
  const controls = useControls('Post-Processing', {
    enabled: {
      value: enabled,
      label: 'Enable All Effects'
    },
    bloomThreshold: {
      value: bloomConfig.threshold,
      min: 0,
      max: 1,
      step: 0.01
    },
    bloomStrength: {
      value: bloomConfig.strength,
      min: 0,
      max: 3,
      step: 0.01
    },
    bloomRadius: {
      value: bloomConfig.radius,
      min: 0,
      max: 1,
      step: 0.01
    },
    lensflareEnabled: {
      value: lensflareConfig.enabled
    },
    lensflareThreshold: {
      value: lensflareConfig.threshold,
      min: 0,
      max: 1,
      step: 0.01
    },
    lensflareGhostAttenuation: {
      value: lensflareConfig.ghostAttenuationFactor,
      min: 1,
      max: 50,
      step: 1
    },
    lensflareGhostSpacing: {
      value: lensflareConfig.ghostSpacing,
      min: 0,
      max: 1,
      step: 0.01
    },
    lensflareBlur: {
      value: lensflareConfig.blur,
      min: 0,
      max: 16,
      step: 1
    },
    chromaticAbEnabled: {
      value: chromaticAberrationConfig.enabled
    },
    chromaticAbAmount: {
      value: chromaticAberrationConfig.amount,
      min: 0,
      max: 0.05,
      step: 0.001
    },
    chromaticAbAngle: {
      value: chromaticAberrationConfig.angle,
      min: 0,
      max: Math.PI * 2,
      step: 0.01
    },
    chromaticAbRadialIntensity: {
      value: chromaticAberrationConfig.radialIntensity,
      min: 0,
      max: 3,
      step: 0.1,
      label: 'CA Radial Intensity'
    },
    crtEnabled: {
      value: cameraTransitionState.active,
      label: 'CRT Effect (Auto during transitions)',
      disabled: true
    },
    crtScanlineIntensity: {
      value: crtConfig.scanlineIntensity,
      min: 0,
      max: 1,
      step: 0.01,
      label: 'Scanline Intensity'
    },
    crtScanlineCount: {
      value: crtConfig.scanlineCount,
      min: 200,
      max: 2000,
      step: 50,
      label: 'Scanline Count'
    },
    crtNoiseIntensity: {
      value: crtConfig.noiseIntensity,
      min: 0,
      max: 0.5,
      step: 0.01,
      label: 'Noise Intensity'
    },
    crtDistortionIntensity: {
      value: crtConfig.distortionIntensity,
      min: 0,
      max: 0.1,
      step: 0.001,
      label: 'Distortion Intensity'
    },
    crtVignetteIntensity: {
      value: crtConfig.vignetteIntensity,
      min: 0,
      max: 1,
      step: 0.01,
      label: 'Vignette Intensity'
    }
  }, { hidden: true })

  // Separate effect to update uniform values without rebuilding pipeline
  useEffect(() => {
    // CRITICAL FIX: Only update uniforms if dev mode is enabled
    // This prevents constant node recreation when Leva controls change
    if (!isDevMode) return
    
    // Update uniform values when controls change
    uniformsRef.current.bloomThreshold.value = controls.bloomThreshold
    uniformsRef.current.bloomStrength.value = controls.bloomStrength
    uniformsRef.current.bloomRadius.value = controls.bloomRadius
    uniformsRef.current.lensflareThreshold.value = controls.lensflareThreshold
    uniformsRef.current.lensflareGhostAttenuation.value = controls.lensflareGhostAttenuation
    uniformsRef.current.lensflareGhostSpacing.value = controls.lensflareGhostSpacing
    uniformsRef.current.chromaticAbAmount.value = controls.chromaticAbAmount
    uniformsRef.current.chromaticAbAngle.value = controls.chromaticAbAngle
    uniformsRef.current.chromaticAbRadialIntensity.value = controls.chromaticAbRadialIntensity
    uniformsRef.current.crtScanlineIntensity.value = controls.crtScanlineIntensity
    uniformsRef.current.crtScanlineCount.value = controls.crtScanlineCount
    uniformsRef.current.crtNoiseIntensity.value = controls.crtNoiseIntensity
    uniformsRef.current.crtDistortionIntensity.value = controls.crtDistortionIntensity
    uniformsRef.current.crtVignetteIntensity.value = controls.crtVignetteIntensity
  }, [isDevMode, controls.bloomThreshold, controls.bloomStrength, controls.bloomRadius,
      controls.lensflareThreshold, controls.lensflareGhostAttenuation, controls.lensflareGhostSpacing,
      controls.chromaticAbAmount, controls.chromaticAbAngle, controls.chromaticAbRadialIntensity,
      controls.crtScanlineIntensity, controls.crtScanlineCount, controls.crtNoiseIntensity,
      controls.crtDistortionIntensity, controls.crtVignetteIntensity])

  useEffect(() => {
    // Only apply post-processing for WebGPU renderer
    if (!gl.isWebGPURenderer) {
      return
    }

    // Helper function to dispose TSL nodes
    const disposeNode = (node) => {
      if (!node) return
      try {
        // Dispose textures if present
        if (node.texture && typeof node.texture.dispose === 'function') {
          node.texture.dispose()
        }
        // Dispose render targets if present
        if (node.renderTarget && typeof node.renderTarget.dispose === 'function') {
          node.renderTarget.dispose()
        }
        // Call dispose if available on the node itself
        if (typeof node.dispose === 'function') {
          node.dispose()
        }
      } catch (error) {
        if (import.meta.env.DEV) console.warn('PostProcessing dispose failed:', error)
      }
    }

    // Dispose all previous effect nodes before creating new ones
    disposeNode(scenePassRef.current)
    disposeNode(bloomPassRef.current)
    disposeNode(lensflarePassRef.current)
    disposeNode(chromaticAbPassRef.current)
    disposeNode(emissiveBloomRef.current)
    disposeNode(flareBlurRef.current)
    
    // Clear refs
    scenePassRef.current = null
    lensflarePassRef.current = null
    chromaticAbPassRef.current = null
    emissiveBloomRef.current = null
    flareBlurRef.current = null

    try {
      // Always create PostProcessing instance for WebGPU
      if (!postProcessingRef.current) {
        const postProcessing = new ThreePostProcessing(gl)
        postProcessingRef.current = postProcessing
      }
      
      const postProcessing = postProcessingRef.current
      
      // Create scene pass with MRT (Multiple Render Targets) for emissive.
      const scenePass = pass(scene, camera)
      scenePassRef.current = scenePass
      
      // Set up MRT using the output and emissive nodes from TSL
      scenePass.setMRT(mrt({
        output: output,
        emissive: emissive
      }))
      
      // Now get the texture nodes from the MRT outputs
      const scenePassColor = scenePass.getTextureNode('output')
      const emissivePass = scenePass.getTextureNode('emissive')
      
      if (enabled) {
        // Apply bloom effect to full scene with uniform nodes
        const bloomPass = bloom(
          scenePassColor, 
          uniformsRef.current.bloomStrength,
          uniformsRef.current.bloomRadius,
          uniformsRef.current.bloomThreshold
        )
        bloomPassRef.current = bloomPass
        
        // Lens flare setup - apply to emissive pass
        let finalPass = bloomPass
        
        if (lensflareConfig.enabled) {
          // Create separate bloom for emissive (for lens flare)
          const emissiveBloom = bloom(emissivePass, 1, 1)
          emissiveBloomRef.current = emissiveBloom
          
          const flarePass = lensflare(emissiveBloom, {
            threshold: uniformsRef.current.lensflareThreshold,
            ghostAttenuationFactor: uniformsRef.current.lensflareGhostAttenuation,
            ghostSpacing: uniformsRef.current.lensflareGhostSpacing
          })
          lensflarePassRef.current = flarePass
          
          // Optional blur for better flare quality
          const blurredFlare = lensflareConfig.blur > 0 
            ? gaussianBlur(flarePass, lensflareConfig.blur)
            : flarePass
          flareBlurRef.current = blurredFlare
          
          // Combine scene bloom + lens flare
          finalPass = bloomPass.add(blurredFlare)
        }
        
        // Apply chromatic aberration
        let outputPass = scenePassColor.add(finalPass)
        
        if (chromaticAberrationConfig.enabled) {
          // Apply radial chromatic aberration to the composed output
          outputPass = radialRGBShift(
            outputPass, 
            uniformsRef.current.chromaticAbAmount, 
            uniformsRef.current.chromaticAbAngle,
            uniformsRef.current.chromaticAbRadialIntensity
          )
          chromaticAbPassRef.current = outputPass
        }
        
        // Always bake the CRT node into the pipeline and blend via crtOpacity uniform.
        // crtOpacity=0 → pure outputPass, crtOpacity=1 → full CRT.
        // This way the node is never created/disposed on each transition — the opacity
        // uniform is all that changes, which is a zero-cost uniform upload per frame.
        const crtEffect = crt(
          outputPass,
          uniformsRef.current.crtScanlineIntensity,
          uniformsRef.current.crtScanlineCount,
          uniformsRef.current.crtNoiseIntensity,
          uniformsRef.current.crtDistortionIntensity,
          uniformsRef.current.crtVignetteIntensity,
          crtTimeRef.current
        )
        crtNodeRef.current = crtEffect
        const blendedOutput = mix(outputPass, crtEffect, uniformsRef.current.crtOpacity)
        
        outputNodeRef.current = blendedOutput
        
        // Set output: original scene + effects + CRT blend
        postProcessing.outputNode = blendedOutput
        postProcessing.needsUpdate = true
        invalidate()
        
      } else {
        // No effects - just pass through the scene
        outputNodeRef.current = scenePassColor
        postProcessing.outputNode = scenePassColor
        invalidate()
        bloomPassRef.current = null
      }
      
      // Store scene pass color for CRT application
      scenePassColorRef.current = scenePassColor
      
    } catch (error) {
      if (import.meta.env.DEV) console.warn('PostProcessing setup failed:', error)
    }

    return () => {
      // Helper function to dispose TSL nodes (local to cleanup)
      const disposeNode = (node) => {
        if (!node) return
        try {
          if (node.texture && typeof node.texture.dispose === 'function') {
            node.texture.dispose()
          }
          if (node.renderTarget && typeof node.renderTarget.dispose === 'function') {
            node.renderTarget.dispose()
          }
          if (typeof node.dispose === 'function') {
            node.dispose()
          }
        } catch (error) {
          if (import.meta.env.DEV) console.warn('PostProcessing dispose failed:', error)
        }
      }
      
      // Dispose all TSL nodes on unmount
      disposeNode(scenePassRef.current)
      disposeNode(bloomPassRef.current)
      disposeNode(lensflarePassRef.current)
      disposeNode(chromaticAbPassRef.current)
      disposeNode(emissiveBloomRef.current)
      disposeNode(flareBlurRef.current)
      disposeNode(crtNodeRef.current)
      
      // Cleanup PostProcessing instance
      if (postProcessingRef.current) {
        try {
          postProcessingRef.current.dispose()
          postProcessingRef.current = null
        } catch (error) {
          if (import.meta.env.DEV) console.warn('PostProcessing dispose failed:', error)
        }
      }
      
      // Clear all refs
      scenePassRef.current = null
      bloomPassRef.current = null
      lensflarePassRef.current = null
      chromaticAbPassRef.current = null
      emissiveBloomRef.current = null
      flareBlurRef.current = null
      crtNodeRef.current = null
      outputNodeRef.current = null
      scenePassColorRef.current = null
    }
  }, [gl, scene, camera, enabled, lensflareConfig.enabled, lensflareConfig.blur, chromaticAberrationConfig.enabled])
  // `camera` is included so the pipeline captures the correct PerspectiveCamera object.
  // R3F replaces the default camera with the PerspectiveCamera once at startup (when
  // Camera.jsx mounts with makeDefault). After that the camera object identity is stable —
  // location/config transitions mutate the existing camera in-place and do NOT produce a
  // new object — so this causes exactly one pipeline rebuild at startup, never during transitions.

  // CRT effect: 0.5s after transition starts → on for 1s → off.
  const crtOpacityTargetRef = useRef(0)
  const crtElapsedRef = useRef(0)
  const crtWasActiveRef = useRef(false)

  // Use custom PostProcessing renderer - this replaces R3F's render
  useFrame(({ gl, clock }, delta) => {
    if (postProcessingRef.current && gl.isWebGPURenderer) {
      try {
        const transitionActive = cameraTransitionState.active && enabled

        // Track elapsed time within the transition to drive fixed CRT window
        if (transitionActive) {
          if (!crtWasActiveRef.current) {
            crtElapsedRef.current = 0
            crtWasActiveRef.current = true
          }
          crtElapsedRef.current += delta
        } else {
          crtWasActiveRef.current = false
        }

        // CRT on from 0.5s → 1.0s after transition begins
        const e = crtElapsedRef.current
        crtOpacityTargetRef.current = (transitionActive && e >= 0.5 && e < 1.0) ? 1 : 0

        // Lerp crtOpacity toward target — fast fade in/out, no pipeline rebuild
        const opacityTarget = crtOpacityTargetRef.current
        const currentOpacity = uniformsRef.current.crtOpacity.value
        if (Math.abs(currentOpacity - opacityTarget) > 0.001) {
          uniformsRef.current.crtOpacity.value += (opacityTarget - currentOpacity) * Math.min(delta * 8, 1)
        } else if (currentOpacity !== opacityTarget) {
          uniformsRef.current.crtOpacity.value = opacityTarget
        }

        // Update CRT noise animation time when the effect is visible
        if (uniformsRef.current.crtOpacity.value > 0 && crtNodeRef.current) {
          crtTimeRef.current = clock.getElapsedTime() * 0.5
          if (crtNodeRef.current.time) {
            crtNodeRef.current.time.value = crtTimeRef.current
          }
        }
        
        postProcessingRef.current.render()
        
        // CRITICAL FIX: Reset renderer.info.render to prevent memory leak
        if (gl.info && gl.info.reset) {
          gl.info.reset()
        }
        
        // REMOVED: onSubmittedWorkDone() Promise leak
        // The experimental GPU queue flushing was creating 60 promises/second
        // This was causing memory growth in Chrome GPU Process
        
        return true // Skip R3F's default render
      } catch (err) {
        if (import.meta.env.DEV) console.error('Error during post-processing render:', err)
        return false
      }
    }
  }, 1)

  return null
}